import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Send, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { useLanguage, translations } from "@/i18n/LanguageContext";

const ContactSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", company: "", service: "", message: "" });
  const { t } = useLanguage();
  const ct = translations.contact;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    toast.success(t(ct.toastSuccess));
  };

  if (submitted) {
    return (
      <section id="kontakt-skjema" ref={ref} className="section-padding py-24 md:py-36 bg-envir-charcoal rounded-t-3xl">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }}>
            <CheckCircle className="w-16 h-16 text-primary mx-auto mb-6" />
            <h2 className="heading-md text-primary-foreground mb-4">{t(ct.thankYouTitle)}</h2>
            <p className="body-md text-primary-foreground/60">{t(ct.thankYouText)}</p>
            <button
              onClick={() => { setSubmitted(false); setFormData({ name: "", email: "", phone: "", company: "", service: "", message: "" }); }}
              className="mt-8 text-sm font-body font-500 text-primary hover:text-primary/80 transition-colors underline underline-offset-4"
            >
              {t(ct.sendAnother)}
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="kontakt-skjema" ref={ref} className="section-padding py-24 md:py-36 bg-envir-charcoal rounded-t-3xl">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-2 gap-16 md:gap-24">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7 }}>
            <span className="text-sm font-body font-500 tracking-widest uppercase text-primary mb-4 block">{t(ct.label)}</span>
            <h2 className="heading-lg text-primary-foreground mb-6">{t(ct.title)}</h2>
            <p className="body-lg text-primary-foreground/60 mb-10">{t(ct.description)}</p>

            <div className="space-y-6 text-sm font-body text-primary-foreground/40">
              <div>
                <span className="text-primary-foreground/70 font-500 block mb-1">{t(ct.phone)}</span>
                <a href="tel:+4755200400" className="hover:text-primary transition-colors">+47 55 200 400</a>
              </div>
              <div>
                <span className="text-primary-foreground/70 font-500 block mb-1">{t(ct.email)}</span>
                <a href="mailto:post@envir.com" className="hover:text-primary transition-colors">post@envir.com</a>
              </div>
              <div>
                <span className="text-primary-foreground/70 font-500 block mb-1">{t(ct.address)}</span>
                <span>Sjøkrigsskoleveien 15, 5165 Laksevåg</span>
              </div>
              <div>
                <span className="text-primary-foreground/70 font-500 block mb-1">{t(ct.hours)}</span>
                <span>{t(ct.hoursValue)}</span>
              </div>
            </div>
          </motion.div>

          <motion.form onSubmit={handleSubmit} initial={{ opacity: 0, x: 20 }} animate={isInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.7, delay: 0.2 }} className="space-y-5">
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="block text-xs font-body font-500 text-primary-foreground/60 mb-2 uppercase tracking-wider">{t(ct.form.name)} *</label>
                <input id="name" name="name" type="text" required value={formData.name} onChange={handleChange} className="w-full bg-primary-foreground/5 border border-primary-foreground/10 rounded px-4 py-3 text-sm font-body text-primary-foreground placeholder:text-primary-foreground/20 focus:outline-none focus:border-primary/50 transition-colors" placeholder={t(ct.form.namePlaceholder)} />
              </div>
              <div>
                <label htmlFor="company" className="block text-xs font-body font-500 text-primary-foreground/60 mb-2 uppercase tracking-wider">{t(ct.form.company)}</label>
                <input id="company" name="company" type="text" value={formData.company} onChange={handleChange} className="w-full bg-primary-foreground/5 border border-primary-foreground/10 rounded px-4 py-3 text-sm font-body text-primary-foreground placeholder:text-primary-foreground/20 focus:outline-none focus:border-primary/50 transition-colors" placeholder={t(ct.form.companyPlaceholder)} />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="email" className="block text-xs font-body font-500 text-primary-foreground/60 mb-2 uppercase tracking-wider">{t(ct.form.email)} *</label>
                <input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} className="w-full bg-primary-foreground/5 border border-primary-foreground/10 rounded px-4 py-3 text-sm font-body text-primary-foreground placeholder:text-primary-foreground/20 focus:outline-none focus:border-primary/50 transition-colors" placeholder={t(ct.form.emailPlaceholder)} />
              </div>
              <div>
                <label htmlFor="phone" className="block text-xs font-body font-500 text-primary-foreground/60 mb-2 uppercase tracking-wider">{t(ct.form.phone)}</label>
                <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} className="w-full bg-primary-foreground/5 border border-primary-foreground/10 rounded px-4 py-3 text-sm font-body text-primary-foreground placeholder:text-primary-foreground/20 focus:outline-none focus:border-primary/50 transition-colors" placeholder={t(ct.form.phonePlaceholder)} />
              </div>
            </div>

            <div>
              <label htmlFor="service" className="block text-xs font-body font-500 text-primary-foreground/60 mb-2 uppercase tracking-wider">{t(ct.form.service)}</label>
              <select id="service" name="service" value={formData.service} onChange={handleChange} className="w-full bg-primary-foreground/5 border border-primary-foreground/10 rounded px-4 py-3 text-sm font-body text-primary-foreground focus:outline-none focus:border-primary/50 transition-colors appearance-none">
                <option value="" className="bg-envir-charcoal">{t(ct.form.servicePlaceholder)}</option>
                {ct.serviceOptions.map((opt, i) => (
                  <option key={i} value={t(opt)} className="bg-envir-charcoal">{t(opt)}</option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="message" className="block text-xs font-body font-500 text-primary-foreground/60 mb-2 uppercase tracking-wider">{t(ct.form.message)} *</label>
              <textarea id="message" name="message" required rows={5} value={formData.message} onChange={handleChange} className="w-full bg-primary-foreground/5 border border-primary-foreground/10 rounded px-4 py-3 text-sm font-body text-primary-foreground placeholder:text-primary-foreground/20 focus:outline-none focus:border-primary/50 transition-colors resize-none" placeholder={t(ct.form.messagePlaceholder)} />
            </div>

            <button type="submit" className="inline-flex items-center gap-2 px-8 py-4 bg-primary text-primary-foreground font-display font-500 text-sm tracking-wide rounded hover:bg-primary/90 transition-all duration-300 hover:-translate-y-0.5">
              {t(ct.form.submit)}
              <Send className="w-4 h-4" />
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
